import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import SignaturePadField from "../components/SignaturePadField";
import {
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  PenLine,
  User,
  FileText,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Stethoscope,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDataIt(raw) {
  if (!raw) return "N/D";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.split("T")[0].split("-");
    return `${d}/${m}/${y}`;
  }
  try {
    return new Date(raw).toLocaleDateString("it-IT");
  } catch {
    return raw;
  }
}

function getNomeOspite(fascicolo) {
  if (!fascicolo) return "";
  if (fascicolo.nome_completo) return fascicolo.nome_completo;
  const ana = fascicolo.anagrafica || {};
  return [ana.nome, ana.cognome].filter(Boolean).join(" ").trim();
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-1.5 text-xs">
      <span className="text-slate-400 shrink-0">{label}:</span>
      <span className="text-white font-medium truncate">{value || "N/D"}</span>
    </div>
  );
}

export default function FirmaDelegaMedico() {
  const { fascicolo_id: fascicoloId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fascicolo, setFascicolo] = useState(null);
  const [delegaStatus, setDelegaStatus] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [luogo, setLuogo] = useState("");
  const [testoOpen, setTestoOpen] = useState(false);

  const sigPadRef = useRef(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fascicoloId, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fascicoloRes, statusRes, templateRes, strutture] = await Promise.all([
        axios.get(`${API_URL}/api/fascicoli-assistenziali/${fascicoloId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/deblistering/delega/check/${fascicoloId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/deblistering/delega-template`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/strutture`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setFascicolo(fascicoloRes.data);
      setDelegaStatus(statusRes.data);
      setTemplateData(templateRes.data);

      const strutturaList = strutture.data?.strutture || [];
      const primary = strutturaList.find((s) => s.is_primary) || strutturaList[0];
      if (primary?.comune) setLuogo(primary.comune);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!luogo.trim()) {
      toast.error("Inserisci il luogo della firma");
      return;
    }
    if (sigPadRef.current?.isEmpty()) {
      toast.error("Disegna la firma prima di procedere");
      return;
    }
    setSaving(true);
    try {
      const firmaFamiliare = sigPadRef.current.toDataURL("image/png");
      await axios.post(
        `${API_URL}/api/deblistering/delega/sign`,
        { fascicolo_id: fascicoloId, firma_familiare: firmaFamiliare, luogo: luogo.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Delega firmata con successo!");
      navigate("/deblistering?tab=ospiti");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella firma della delega");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-400" />
      </div>
    );
  }

  /* ─── Template non configurato ─── */
  if (!delegaStatus?.has_template) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <AlertCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Template non configurato</h2>
          <p className="text-slate-500 mb-6 text-sm">
            Configura il testo della delega nella tab Impostazioni del Deblistering.
          </p>
          <Button onClick={() => navigate("/deblistering")}>Vai al Deblistering</Button>
        </div>
      </div>
    );
  }

  /* ─── Già firmata ─── */
  if (delegaStatus?.has_signed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Delega già firmata</h2>
          <p className="text-slate-500 mb-6 text-sm">
            Firmata il {new Date(delegaStatus.signed_at).toLocaleDateString("it-IT")}
          </p>
          <Button variant="outline" onClick={() => navigate("/deblistering?tab=ospiti")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Deblistering
          </Button>
        </div>
      </div>
    );
  }

  const anagrafica = fascicolo?.anagrafica || {};
  const sanitaria = fascicolo?.sanitaria || {};
  const nomeOspite = getNomeOspite(fascicolo);
  const oggi = new Date().toLocaleDateString("it-IT");

  const medicoNome = sanitaria.medico_curante || "";
  const medicoIndirizzo = sanitaria.medico_curante_indirizzo_studio || "";
  const medicoTel = sanitaria.medico_curante_telefono || "";
  const medicoEmail =
    sanitaria.medico_curante_email_blisteraggio ||
    sanitaria.medico_curante_email_personale ||
    "";

  const missingFields = [];
  if (!nomeOspite) missingFields.push({ section: "Dati Ospite", field: "Nome e Cognome" });
  if (!anagrafica.codice_fiscale) missingFields.push({ section: "Dati Ospite", field: "Codice Fiscale" });
  if (!anagrafica.data_nascita) missingFields.push({ section: "Dati Ospite", field: "Data di Nascita" });
  if (!medicoNome)
    missingFields.push({ section: "Dati Medico", field: "Nome medico curante" });
  const canSign = missingFields.length === 0;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
        <button
          onClick={() => navigate("/deblistering?tab=ospiti")}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Esci
        </button>
        <span className="text-white font-semibold text-sm tracking-wide">Firma Delega Medico</span>
        <span className="text-slate-400 text-sm truncate max-w-[160px]">{nomeOspite}</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl px-4 py-5 space-y-4">

          {/* ── Riepilogo ── */}
          <div className="border border-slate-700 rounded-xl bg-slate-800/60 backdrop-blur overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
              <FileText className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-medium text-white">Riepilogo delega</span>
            </div>

            <div className="px-4 pt-3 pb-4 space-y-4 text-sm">
              {/* Dati ospite */}
              <div className="space-y-1">
                <p className="text-xs text-indigo-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                  <User className="w-3 h-3" /> Ospite
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                  <SummaryRow label="Nome" value={nomeOspite} />
                  <SummaryRow label="C.F." value={anagrafica.codice_fiscale} />
                  <SummaryRow label="Nato il" value={formatDataIt(anagrafica.data_nascita)} />
                </div>
              </div>

              {/* Dati medico */}
              <div className="flex items-start gap-3 bg-indigo-900/30 border border-indigo-800/40 rounded-lg px-3 py-2">
                <Stethoscope className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  {medicoNome && (
                    <span className="text-slate-300">
                      Medico: <span className="text-indigo-300 font-bold">{medicoNome}</span>
                    </span>
                  )}
                  {medicoIndirizzo && (
                    <span className="text-slate-300">
                      Studio: <span className="text-white font-medium">{medicoIndirizzo}</span>
                    </span>
                  )}
                  {medicoTel && (
                    <span className="text-slate-300">
                      Tel.: <span className="text-white font-medium">{medicoTel}</span>
                    </span>
                  )}
                  {medicoEmail && (
                    <span className="text-slate-300">
                      Email: <span className="text-white font-medium">{medicoEmail}</span>
                    </span>
                  )}
                  {!medicoNome && !medicoIndirizzo && !medicoTel && !medicoEmail && (
                    <span className="text-amber-400">Dati medico non presenti nel fascicolo</span>
                  )}
                </div>
              </div>

              {/* Testo delega — collassabile */}
              {templateData?.body_text && (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setTestoOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-700/50 hover:bg-slate-700 transition-colors text-xs font-medium text-slate-300"
                  >
                    <span className="flex items-center gap-1.5">
                      <FileText className="w-3 h-3 text-indigo-400" />
                      Leggi testo della delega
                    </span>
                    {testoOpen
                      ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                      : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                  </button>
                  {testoOpen && (
                    <div className="max-h-56 overflow-y-auto px-3 py-2 bg-slate-900/60 text-slate-300 text-xs leading-relaxed whitespace-pre-wrap">
                      {templateData.body_text}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Avviso dati mancanti ── */}
          {missingFields.length > 0 && (
            <div className="bg-red-900/40 border border-red-700/60 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-semibold text-sm">Dati obbligatori mancanti</p>
                  <p className="text-red-400 text-xs mt-0.5">
                    Completa il fascicolo prima di procedere con la firma.
                  </p>
                </div>
              </div>
              <ul className="space-y-1 pl-8">
                {missingFields.map((f, i) => (
                  <li key={i} className="text-xs text-red-300 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                    <span className="text-red-400">{f.section}:</span>
                    <span className="font-medium">{f.field}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(`/fascicoli-assistenziali/${fascicoloId}`)}
                className="flex items-center gap-1.5 text-xs text-red-300 hover:text-white underline underline-offset-2 pl-8 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Apri fascicolo e completa i dati
              </button>
            </div>
          )}

          {/* ── Area firma ── */}
          <div className={`bg-white rounded-2xl shadow-2xl overflow-hidden transition-opacity ${!canSign ? "opacity-50 pointer-events-none select-none" : ""}`}>
            <div className="bg-indigo-600 px-6 py-4">
              <h1 className="text-white text-lg font-bold flex items-center gap-2">
                <PenLine className="w-5 h-5" />
                Firma per autorizzazione
              </h1>
            </div>

            <div className="p-6 space-y-5">
              {/* Luogo + Data */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-slate-700 font-medium mb-1 block">
                    Luogo <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                    <Input
                      value={luogo}
                      onChange={(e) => setLuogo(e.target.value)}
                      placeholder="Es: Roma"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1.5 text-sm text-slate-500 pb-2">
                  <Calendar className="w-4 h-4" />
                  <span>{oggi}</span>
                </div>
              </div>

              {/* Canvas firma */}
              <div>
                <Label className="text-slate-700 font-medium mb-1 block">
                  Firma{" "}
                  <Badge className="bg-red-100 text-red-700 text-xs ml-1">Obbligatoria</Badge>
                </Label>
                <SignaturePadField
                  ref={sigPadRef}
                  backgroundColor="rgb(248,250,252)"
                  containerClassName="relative border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 overflow-hidden"
                  canvasClassName="w-full touch-none"
                  canvasStyle={{ width: "100%", height: "220px", display: "block" }}
                  actionsClassName="flex flex-wrap items-center justify-between gap-2 mt-2"
                />
              </div>

              {/* Pulsante firma */}
              <Button
                onClick={handleSign}
                disabled={saving || !canSign}
                className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 shadow-lg disabled:opacity-60"
              >
                {saving ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generazione delega...</>
                ) : (
                  <><Check className="w-5 h-5 mr-2" />Firma e genera delega</>
                )}
              </Button>

              <p className="text-center text-xs text-slate-400">
                Firmando si autorizza il trattamento dei dati sanitari nell'ambito del servizio di deblistering.
                La firma dell'ospite verrà applicata al documento e salvata automaticamente.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
